import type { PlaceBetInput } from '../services/markets.service';

const STORAGE_KEY = 'ponyup-pending-bets';
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export interface QueuedBet extends PlaceBetInput {
    queuedAt: number;
}

/**
 * Bets that were submitted but never confirmed.
 *
 * Backyard cell service drops mid-request constantly, and a bet the person
 * believes they placed has to survive both a reload and a closed tab. Replay is
 * safe because every entry keeps the idempotency key it was first submitted
 * with: the server collapses a repeat to the original bet rather than placing a
 * second one, so flushing twice cannot double-stake.
 *
 * Entries older than a few hours are dropped rather than replayed -- the market
 * has certainly closed, and posting a stale bet the moment someone reconnects
 * would be worse than losing it.
 */
const read = (): QueuedBet[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return (parsed as QueuedBet[]).filter(
            (bet) => Date.now() - bet.queuedAt < MAX_AGE_MS,
        );
    } catch {
        return [];
    }
};

const write = (bets: QueuedBet[]): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(bets));
    } catch {
        // A full or blocked store is not a reason to lose the bet in flight.
    }
};

export const pendingBets = (): QueuedBet[] => read();

export const enqueueBet = (bet: PlaceBetInput): void => {
    const queue = read();
    // Keyed by idempotency key, so a retry of the same attempt replaces rather
    // than stacking.
    const next = queue.filter((entry) => entry.idempotencyKey !== bet.idempotencyKey);
    next.push({ ...bet, queuedAt: Date.now() });
    write(next);
};

export const dequeueBet = (idempotencyKey: string): void => {
    write(read().filter((entry) => entry.idempotencyKey !== idempotencyKey));
};
